import {forwardRef, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '../lib/cn';
import {Switch} from '../switch';

export interface BoxOptionSelectorSwitchProps {
  value: string;
}

export interface BoxOptionSelectorProps {
  id: string;
  title: string;
  description: string;
  value: string;
  switch?: BoxOptionSelectorSwitchProps;
  onChange: (value: string) => void;
  disabled?: boolean;
  selectedValue: string;
  optionalLabel?: string;
}

export const BoxOptionSelector = forwardRef<HTMLDivElement, BoxOptionSelectorProps>(
  (
    {
      id,
      title,
      description,
      value,
      switch: switchProps,
      onChange,
      disabled = false,
      selectedValue,
      optionalLabel,
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const [isSwitchActive, setIsSwitchActive] = useState(false);

    const isSelected = switchProps?.value === selectedValue || value === selectedValue;

    const handleClick = () => {
      if (disabled) return;
      onChange(isSwitchActive ? (switchProps?.value ?? value) : value);
    };

    const handleSwitchChange = (checked: boolean) => {
      if (disabled) return;
      setIsSwitchActive(checked);
      onChange(checked ? (switchProps?.value ?? value) : value);
    };

    useEffect(() => {
      setIsSwitchActive(selectedValue === switchProps?.value);
    }, [selectedValue, switchProps?.value]);

    return (
      <div
        ref={ref}
        data-slot="box-option-selector"
        className={cn(
          'flex min-h-[116px] w-full max-w-[400px] cursor-pointer flex-col gap-4 rounded-lg border border-[var(--box-option-border)] bg-[var(--box-option-bg)] p-4 transition-all duration-200 hover:bg-[var(--box-option-hover-bg)]',
          isSelected &&
            'border-[var(--box-option-selected-border)] bg-[var(--box-option-selected-bg)] hover:bg-[var(--box-option-selected-bg)]',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={handleClick}
      >
        <div className="flex w-full items-center justify-between gap-5">
          <h3 className="whitespace-nowrap text-base font-semibold leading-6">{title}</h3>
          {switchProps && isSelected && (
            <div
              onClick={event => event.stopPropagation()}
              className="flex items-center gap-2"
            >
              <Switch
                id={`optional-${id}`}
                value={isSwitchActive}
                disabled={disabled}
                size="sm"
                onChange={handleSwitchChange}
              />
              <span
                className={cn(
                  'rounded-3xl px-3 py-1 text-sm font-medium leading-4 mix-blend-multiply',
                  isSwitchActive
                    ? 'bg-[var(--chekin-color-surface-pressed)] text-[var(--chekin-color-brand-blue)]'
                    : 'bg-[var(--chekin-color-surface-input-empty)] text-[var(--chekin-color-gray-2)]',
                )}
              >
                {optionalLabel || t('optional')}
              </span>
            </div>
          )}
        </div>
        <p className="w-full text-sm font-medium leading-4 text-[var(--chekin-color-gray-1)]">
          {description}
        </p>
      </div>
    );
  },
);

BoxOptionSelector.displayName = 'BoxOptionSelector';
